import { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useGetAuthoringComponentQuery, useGetAuthoringComponentRevisionQuery } from '../api/authoring'
import { asSchema, orderedProperties } from '../model/schema'
import { formableIntents, typeOf } from '../model/manifest'
import { filterMember } from '../model/records'
import { Loading, QueryError } from '../ui/QueryState'
import { IntentForm } from './IntentForm'
import { MembershipFilter } from './MembershipFilter'
import { SchemaForm } from './SchemaForm'
import type { EditorProps } from './types'
import { shown } from '../api/useIntent'

/**
 * A pattern placement or repeater: the properties it supplies to the
 * pattern it runs.
 *
 * **The one editor whose form is typed by another document.** Everywhere
 * else the manifest answers what a control draws — a type's fields, an
 * intent's payload — and that is exactly what it cannot do here. A
 * placement's `props` are a map on the Pattern type, so the reflected
 * schema says `object` and stops; what may be in it is declared by the
 * *pattern*, which is a different document, chosen per placement, and
 * different for every declaration in the tenant. So the schema is read
 * from the declaration this placement refers to.
 *
 * It is derived there rather than constructed here (`derived.propsSchema`),
 * which is what keeps a form from drifting out of what the write path
 * enforces: the bounds, the enums and the ordering come off the same
 * construction a record's own form does, and a client reimplementing them
 * would be a second reading of one declaration.
 *
 * **No type name is held to find the declaration.** The manifest says
 * which type declares patterns, and the placement's references say which
 * component of it this placement runs — the same asking rather than
 * knowing the uploader and the type designer do.
 */
export function PatternEditor(props: EditorProps) {
  const { tenant, manifest, typeName, fields, references, busy, send } = props

  // **Everything else the type declares, drawn beside the properties.**
  // This editor built the one intent it is about and drew nothing else,
  // so "Show only some of the records" was declared, offered by the
  // platform, and reachable from no screen. The form, gallery and grid
  // editors all draw their remainder; this one now does too, and it is
  // outside `Properties` because that returns early while a block has
  // no pattern yet and a filter is worth setting before it has one.
  const rest = formableIntents(manifest, typeName, fields).filter(
    (intent) => !orderedProperties(asSchema(intent.payload), asSchema(intent.payload) ?? {}).some(
      (property) => property.name === PROPS,
    ),
  )

  return (
    <Stack spacing={3}>
      <Properties {...props} />
      {rest.map((intent) => {
        const member = filterMember(asSchema(intent.payload))
        return member ? (
          <MembershipFilter
            key={intent.name}
            tenant={tenant}
            manifest={manifest}
            intent={intent}
            member={member}
            fields={fields}
            references={references}
            busy={busy}
            send={send}
          />
        ) : (
          <IntentForm key={intent.name} intent={intent} fields={fields} busy={busy} send={send} />
        )
      })}
    </Stack>
  )
}

function Properties({ tenant, manifest, typeName, fields, references, busy, send }: EditorProps) {
  const declaringType = manifest?.types.find((entry) => entry.declaresPatterns)?.name
  const declaration = references.find((reference) => reference.type === declaringType)

  // A pinned placement runs the revision it is pinned to, so that is the
  // declaration whose properties this form must draw. Reading the latest
  // instead would offer an author a field the expansion will never see.
  // Both hooks are declared and one is skipped: a hook cannot be
  // conditional, and which of the two answers is a fact about the pin.
  const pinned = declaration?.revision !== undefined
  const head = useGetAuthoringComponentQuery(
    { tenant, id: declaration?.id ?? '' },
    { skip: !declaration || pinned },
  )
  const atRevision = useGetAuthoringComponentRevisionQuery(
    { tenant, id: declaration?.id ?? '', revision: declaration?.revision ?? 1 },
    { skip: !declaration || !pinned },
  )
  const read = pinned ? atRevision : head
  const document = read.data

  const schema = asSchema(document?.derived?.[PROPS_SCHEMA])
  const stored = (fields[PROPS] ?? {}) as Record<string, unknown>
  const starting = useMemo(() => ({ ...stored }), [stableKey(stored)])

  const [value, setValue] = useState<Record<string, unknown>>(starting)
  useEffect(() => setValue(starting), [starting])

  // The intent that writes them, found by the member it carries rather
  // than by its wire name — the same join every derived control makes.
  const writes = typeOf(manifest, typeName)?.intents.find((intent) => {
    const payload = asSchema(intent.payload)
    return payload ? orderedProperties(payload, payload).some((property) => property.name === PROPS) : false
  })

  if (!declaration) {
    return (
      <Alert severity="info">
        This block runs no pattern yet. Choose a declaration under <strong>References</strong> — it says what this
        block presents and which properties it supplies, and a block without one expands to nothing.
      </Alert>
    )
  }

  if (read.isLoading) return <Loading label="Reading the declaration…" />
  if (read.error) return <QueryError error={read.error} title="Could not read the declaration" />

  const properties = schema ? orderedProperties(schema, schema) : []
  const dirty = stableKey(value) !== stableKey(starting)

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography variant="h3">Properties</Typography>
        <Chip
          size="small"
          variant="outlined"
          label={`${labelOf(document?.fields) ?? declaration.id}${pinned ? ` · r${declaration.revision}` : ''}`}
        />
      </Stack>

      {!schema && (
        <Alert severity="warning">
          This declaration publishes no property schema. It was written by a build that did not derive one; the
          placement still expands, and its properties cannot be edited here.
        </Alert>
      )}

      {schema && properties.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          This pattern declares no properties. What it presents comes from the records it refers to.
        </Typography>
      )}

      {schema && properties.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary">
            Local to this placement, and typed by the declaration rather than by the placement. A value outside what
            the declaration allows leaves the placement unexpanded rather than being refused here — the rule that
            judges it lives on the other document.
          </Typography>

          <SchemaForm schema={schema} root={schema} value={value} onChange={setValue} disabled={busy} />

          {writes ? (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                disabled={busy || !dirty}
                onClick={() => send(writes.name, { [PROPS]: value }).catch(shown)}
              >
                Set properties
              </Button>
              <Button disabled={busy || !dirty} onClick={() => setValue(starting)}>
                Discard
              </Button>
            </Stack>
          ) : (
            <Alert severity="warning">This type declares no intent that writes properties.</Alert>
          )}
        </>
      )}
    </Stack>
  )
}

/**
 * The member a placement's properties live under, on the placement and in
 * the derivation alike.
 *
 * One name, stated once. It is the reserved scope a declaration's
 * bindings read them through — `$props.lead` — so it is contract
 * vocabulary rather than a guess, and the two readings of it here cannot
 * drift apart.
 */
const PROPS = 'props'

/** The declaration's derived form for its placement properties. */
const PROPS_SCHEMA = 'propsSchema'

/** A declaration's own name for itself, for the chip. */
function labelOf(fields: Record<string, unknown> | undefined): string | undefined {
  const name = fields?.patternName
  return typeof name === 'string' && name ? name : undefined
}

/**
 * Properties reduced to what they will say on the wire, for comparison
 * only — an untouched control and an absent member are one value, so
 * comparing raw would report a freshly opened form as dirty.
 */
function stableKey(value: Record<string, unknown>): string {
  const members = Object.entries(value)
    .filter(([, member]) => member !== undefined && member !== '' && member !== false)
    .sort(([left], [right]) => left.localeCompare(right))
  return JSON.stringify(members)
}
